import React, {
  useEffect,
  useRef,
  useState,
  TextareaHTMLAttributes,
  useLayoutEffect,
  forwardRef,
  useImperativeHandle,
  ChangeEvent,
} from 'react'

import {TypographyVariant, stylesForTypographyVariant, TypographyTextAlign} from './typography'

export interface TypographicTextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly variant?: TypographyVariant
  readonly align?: TypographyTextAlign
}

export const TypographicTextArea = forwardRef<HTMLTextAreaElement, TypographicTextAreaProps>(
  ({variant = 'body1', align = 'left', onChange, ...props}, forwardRef) => {
    const ref = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(forwardRef, () => ref.current!, [ref.current])

    const [hasFocus, setFocus] = useState(false)
    const [isHovered, setHover] = useState(false)
    const disabled = !!props.disabled

    const baseWrapperStyle: React.CSSProperties = {
      borderRadius: '6px',
      boxSizing: 'border-box',
      border: '1px solid #cccccd',
      borderColor: '#cccccd',
    }

    const wrapperStyle: React.CSSProperties = {
      ...baseWrapperStyle,
      ...(hasFocus && !disabled
        ? {
            outline: '3px solid var(--rs-color-focus-ring)',
            borderColor: 'var(--rs-input-focus-border)',
          }
        : {}),
      ...(!hasFocus && isHovered && !disabled
        ? {
            borderColor: 'var(--rs-input-focus-border)',
          }
        : {}),
    }

    useLayoutEffect(() => {
      handleResize()
    }, [])

    useEffect(() => {
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver((entries) => {
          requestAnimationFrame(() => {
            handleResize()
          })
        })
        const currentRef = ref.current
        if (!currentRef) return
        observer.observe(ref.current)
        return () => observer.unobserve(currentRef)
      } else {
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
      }
    }, [ref])

    function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
      handleResize()
      onChange?.(e)
    }

    function handleResize() {
      if (ref.current) {
        ref.current.style.overflow = 'hidden'
        ref.current.style.height = 'auto'
        ref.current.style.height = `${ref.current.scrollHeight}px`
      }
    }

    return (
      <div
        style={wrapperStyle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}>
        <textarea
          ref={ref}
          {...props}
          onFocus={(e) => {
            setFocus(true)
            props.onFocus?.(e as any)
          }}
          onBlur={(e) => {
            setFocus(false)
            props.onBlur?.(e as any)
          }}
          onChange={handleChange}
          style={{
            display: 'block',

            width: '100%',
            resize: 'none',

            /* border moved to wrapper */
            borderColor: 'transparent',
            borderRadius: 6,

            fontFamily: 'inherit',
            lineHeight: 1.375,
            overflow: 'hidden',
            height: 'auto',

            color: 'black',
            textAlign: align,
            ...stylesForTypographyVariant(variant),

            outline: 'none !important',
          }}
          rows={1}
        />
      </div>
    )
  }
)
